import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast } from "sonner";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({}); // slug -> form values

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/products");
      setProducts(r.data);
      const e = {};
      r.data.forEach((p) => { e[p.slug] = { name: p.name, description: p.description, base_price: p.base_price, default_cost: p.default_cost ?? 0, active: p.active }; });
      setEditing(e);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (slug) => {
    try {
      const e = editing[slug];
      await api.patch(`/admin/products/${slug}`, {
        name: e.name,
        description: e.description,
        base_price: Number(e.base_price),
        default_cost: Number(e.default_cost) || 0,
        active: e.active,
      });
      toast.success("Saved");
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const setField = (slug, k, v) =>
    setEditing((prev) => ({ ...prev, [slug]: { ...prev[slug], [k]: v } }));

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold text-[var(--brand-text)] mb-4">Products & Pricing</h1>
      {loading ? (
        <p className="muted-text">Loading…</p>
      ) : (
        <div className="space-y-3">
          {products.map((p) => {
            const e = editing[p.slug] || {};
            return (
              <div key={p.slug} className="bg-white border border-[var(--brand-border)] rounded-2xl p-5"
                data-testid={`product-row-${p.slug}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs uppercase tracking-wider muted-text">{p.slug} · {p.type}</p>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      data-testid={`product-${p.slug}-active`}
                      type="checkbox"
                      checked={!!e.active}
                      onChange={(ev) => setField(p.slug, "active", ev.target.checked)}
                    />
                    Active
                  </label>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-semibold uppercase muted-text">Name</label>
                    <input
                      data-testid={`product-${p.slug}-name`}
                      className="rounded-input mt-1"
                      value={e.name || ""}
                      onChange={(ev) => setField(p.slug, "name", ev.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase muted-text">Description</label>
                    <textarea
                      data-testid={`product-${p.slug}-description`}
                      className="rounded-input mt-1 min-h-[60px]"
                      value={e.description || ""}
                      onChange={(ev) => setField(p.slug, "description", ev.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase muted-text">Base price ($)</label>
                    <input
                      data-testid={`product-${p.slug}-price`}
                      type="number" step="0.01"
                      className="rounded-input mt-1"
                      value={e.base_price ?? ""}
                      onChange={(ev) => setField(p.slug, "base_price", ev.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase muted-text">
                      Default cost / unit ($) <span className="muted-text font-normal normal-case">— admin-only, auto-stamps profit on new paid orders</span>
                    </label>
                    <input
                      data-testid={`product-${p.slug}-default-cost`}
                      type="number" step="0.01" min="0"
                      className="rounded-input mt-1"
                      value={e.default_cost ?? 0}
                      onChange={(ev) => setField(p.slug, "default_cost", ev.target.value)}
                    />
                  </div>
                  <button data-testid={`product-${p.slug}-save`} onClick={() => save(p.slug)} className="cta-btn">
                    Save changes
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
